import { useEffect, useRef } from "preact/hooks";

// Pin a scroll container to the bottom as new content arrives, but only while
// the user is already near the bottom. Scrolling up to read history disables the
// auto-follow until they come back within `thresholdPx` of the bottom — so a
// streaming reply never yanks the viewport down while the user is reading above.
//
// Pass the reactive values that signal "new content" as `deps` (e.g. the chat
// array + streaming flags). The container is re-pinned whenever they change; for
// a token stream the chat array must be a fresh reference per update (it is —
// use-chat-stream patches via setChat(prev => prev.map(...))).
export function useStickToBottom<T extends HTMLElement>(
  deps: unknown[],
  thresholdPx = 48,
) {
  const ref = useRef<T>(null);
  // Whether the user is currently near the bottom (auto-follow enabled).
  const pinnedRef = useRef(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      pinnedRef.current =
        el.scrollHeight - el.scrollTop - el.clientHeight <= thresholdPx;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [thresholdPx]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // An unscrollable container is by definition at the bottom — re-pin so a
    // stale "scrolled away" flag can't survive a content reset (e.g. the chat
    // emptying on a same-component page navigation, where no scroll event
    // fires to correct it).
    if (el.scrollHeight <= el.clientHeight) pinnedRef.current = true;
    if (pinnedRef.current) el.scrollTop = el.scrollHeight;
    // `deps` are caller-provided new-content signals; pinnedRef gates the scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return ref;
}
